import type { Activity, IncidentProposal, StatusError, UiEvent } from "../../server/src/shared-types";

export type ConfirmationOutcome = "approved" | "rejected" | "failed" | "withdrawn";

export type TimelineItem =
  | { type: "message"; id: string; role: "user" | "agent"; text: string; pending?: boolean }
  | { type: "activity"; id: string; activity: Activity; issue?: string }
  | { type: "confirmation"; id: string; proposal: IncidentProposal; outcome?: ConfirmationOutcome }
  | { type: "incident"; id: string; action: "created" | "commented"; identifier: string; title: string; url: string };

export type AgentState = "idle" | "working" | "waiting_user" | "error";

export interface Timeline {
  items: TimelineItem[];
  state: AgentState;
  error?: StatusError;
  ended: boolean;
  pendingConfirmationId?: string;
}

/** Adds events without duplicates (history and stream overlap). */
export function mergeEvents(current: UiEvent[], incoming: UiEvent[]): UiEvent[] {
  const seen = new Set(current.map((e) => e.id));
  const fresh = incoming.filter((e) => !seen.has(e.id) && seen.add(e.id));
  return fresh.length ? [...current, ...fresh] : current;
}

export function buildTimeline(events: UiEvent[], pendingMessages: string[]): Timeline {
  const items: TimelineItem[] = [];
  const confirmations = new Map<string, Extract<TimelineItem, { type: "confirmation" }>>();
  let state: AgentState = "idle";
  let error: StatusError | undefined;
  let ended = false;

  for (const event of events) {
    switch (event.kind) {
      case "message":
        items.push({ type: "message", id: event.id, role: event.role, text: event.text });
        break;
      case "activity":
        items.push({ type: "activity", id: event.id, activity: event.activity, issue: event.issue });
        break;
      case "confirmation_request": {
        const item = { type: "confirmation" as const, id: event.id, proposal: event.proposal };
        confirmations.set(event.id, item);
        items.push(item);
        break;
      }
      case "confirmation_resolved": {
        const item = confirmations.get(event.toolUseId);
        if (item) item.outcome = event.outcome;
        break;
      }
      case "incident":
        items.push({ type: "incident", ...event });
        break;
      case "status":
        state = event.state;
        error = event.state === "error" ? (event.error ?? "temporary") : undefined;
        ended ||= event.final === true;
        break;
    }
  }

  // Sent messages that have not come back through the stream yet (App removes them on the echo).
  pendingMessages.forEach((text, i) =>
    items.push({ type: "message", id: `pending-${i}`, role: "user", text, pending: true }),
  );

  const pendingConfirmation = [...confirmations.values()].find((c) => !c.outcome);
  return {
    // Proposals the backend rejected before they reached the person are not shown.
    items: items.filter((i) => !(i.type === "confirmation" && i.outcome === "withdrawn")),
    state: pendingConfirmation ? "waiting_user" : pendingMessages.length && state !== "error" ? "working" : state,
    error,
    ended,
    pendingConfirmationId: pendingConfirmation?.id,
  };
}
